import { alpha, Grid, IconButton, Toolbar, Typography, useEventCallback } from '@mui/material';
import EnrollmentList from 'shell/components/shared/enrollment-list';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import TableCheckbox from 'shell/components/shared/table-checkbox';
import { HeadCell } from 'shell/components/shared/table/types';
import { noop } from 'shell/utils/noop';
import { CheckboxTable } from 'shell/view-models/checkbox-table';
import AireList from 'system/components/shared/aire-list';
import { SubjectData } from 'shell/models/person';
import { PracticeFamily } from 'shell/models/practice-family';
import CohabitationBondList from 'shell/components/shared/cohabitation-bond-list';
import ParenthoodRelationshipList from 'shell/components/shared/parenthood-relationship-list';
import { Edit } from '@mui/icons-material';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import { ResidenceOrigin } from 'shell/view-models/personal-data';
import CfControlList from './cf-control-list';
import { TypeRegister } from 'practice/constants/type-register';
import filterParenthood from 'shell/components/shared/parenthood-relationship-list/filter-parenthood';

export interface FamilyVM extends CheckboxTable {
  surname: string;
  name: string;
  fiscalCode: string;
  birthDate: string;
  birthplace: string;
  residence: string;
  richiedente?: boolean;
  relationship?: string;
  oldRelationship?: string;
  drivingLicense?: string;
  aire: string;
  auto?: string;
  relationshipElement?: ReactElement;
  drivingLicenseElement?: ReactElement;
  autoElement?: ReactElement;
  aireElement?: ReactElement;
  subject?: SubjectData | PracticeFamily;
  isEditGenerality?: boolean;
  generality?: AccordionGeneralityForm | null;
  selectedFromDb?: boolean;
  idAnprIntestatario?: string;
  intestatario?: boolean;
  isSipo?: boolean;
  posizionePersona?: string;
  isPersonaNonResidente?: boolean;
  tipoCasoPratica?: string;
  residenceOrigin?: ResidenceOrigin;
  tipoLegame?: string;
  idSchedaSoggettoANPR?: string;
  isMaiAnagrafato?: boolean;
  idIntestatario?: string;
  cfControlElement?: ReactElement;
  cfControl?: string;
}

export const headCellsPracticesAssign: HeadCell<FamilyVM, keyof FamilyVM>[] = [
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'fiscalCode',
    label: 'CF',
  },
  {
    id: 'birthDate',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'relationshipElement',
    label: 'Parentela',
  },
  {
    id: 'drivingLicenseElement',
    label: 'Patente',
  },
  {
    id: 'autoElement',
    label: 'Auto',
  },
  {
    id: 'aireElement',
    label: 'AIRE',
  },
  {
    id: 'cfControlElement',
    label: 'Controllo dati AE',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

interface TableFamilyFormProps {
  disabled?: boolean;
  disabledAll?: boolean;
  values: Array<FamilyVM>;
  onChange: (familyMembers: Array<FamilyVM>) => void;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  typePractice?: string;
  disabledResidente?: boolean;
}

const TableFamilyFormComponent = ({
  values,
  onChange,
  disabledAll,
  disabled,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  typePractice,
  disabledResidente,
}: TableFamilyFormProps): React.ReactElement => {
  const onChangeRelationship = useEventCallback((itemSelected: FamilyVM, value?: string) => {
    const newItem: FamilyVM = {
      ...itemSelected,
      relationship: value,
    };

    let practicesNew: Array<FamilyVM> = values?.slice() || [];

    const isSelectedPractice = !!values?.find(
      (item) =>
        item.fiscalCode === itemSelected.fiscalCode &&
        item.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
        item.idIntestatario === itemSelected.idIntestatario
    );

    if (isSelectedPractice) {
      practicesNew = practicesNew.map((operation) =>
        !(
          operation.fiscalCode === itemSelected.fiscalCode &&
          operation.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
          operation.idIntestatario === itemSelected.idIntestatario
        )
          ? operation
          : newItem
      ) as Array<FamilyVM>;

      onChange(practicesNew);
      return;
    }

    practicesNew.push(newItem);
    onChange(practicesNew);
  });

  const navigate = useNavigate();
  const filterTable = useFilterTable<NewPersonVM>();

  const onEditPress = useEventCallback((itemSelected: FamilyVM) => {
    // set step
    filterTable.setValues({
      ...(filterTable.values || {}),
      currentStep: 1,
      fromList: true,
    });

    navigate({
      pathname: generatePath(
        '/pratiche/ricerca/modifica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:tipoPratica/nuova-persona',
        {
          codiceEnte: codeEnte || '',
          tipoRegistro: typeRegister || '',
          idAnnoPratica: yearId?.toString() || '',
          progressivoPratica: progressive?.toString() || '',
          tipoPratica: typePractice || '',
        }
      ),
      search:
        itemSelected.idSchedaSoggettoANPR || itemSelected.idIntestatario
          ? `?${createSearchParams({
              idIntestatario: itemSelected.idIntestatario || '0',
              idAnpr: itemSelected.idSchedaSoggettoANPR || '0',
            })}`
          : '',
    });
  });

  const onChangeDriving = useEventCallback((itemSelected: FamilyVM, value?: string) => {
    const newItem: FamilyVM = {
      ...itemSelected,
      drivingLicense: value,
    };

    let practicesNew: Array<FamilyVM> = values?.slice() || [];

    const isSelectedPractice = !!values?.find(
      (item) =>
        item.fiscalCode === itemSelected.fiscalCode &&
        item.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
        item.idIntestatario === itemSelected.idIntestatario
    );

    if (isSelectedPractice) {
      practicesNew = practicesNew.map((operation) =>
        !(
          operation.fiscalCode === itemSelected.fiscalCode &&
          operation.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
          operation.idIntestatario === itemSelected.idIntestatario
        )
          ? operation
          : newItem
      ) as Array<FamilyVM>;

      onChange(practicesNew);
      return;
    }

    practicesNew.push(newItem);
    onChange(practicesNew);
  });

  const onChangeAuto = useEventCallback((itemSelected: FamilyVM, value?: string) => {
    const newItem: FamilyVM = {
      ...itemSelected,
      auto: value,
    };

    let practicesNew: Array<FamilyVM> = values?.slice() || [];

    const isSelectedPractice = !!values?.find(
      (item) =>
        item.fiscalCode === itemSelected.fiscalCode &&
        item.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
        item.idIntestatario === itemSelected.idIntestatario
    );

    if (isSelectedPractice) {
      practicesNew = practicesNew.map((operation) =>
        !(
          operation.fiscalCode === itemSelected.fiscalCode &&
          operation.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
          operation.idIntestatario === itemSelected.idIntestatario
        )
          ? operation
          : newItem
      ) as Array<FamilyVM>;

      onChange(practicesNew);
      return;
    }

    practicesNew.push(newItem);
    onChange(practicesNew);
  });

  const onChangeCfControl = useEventCallback((itemSelected: FamilyVM, value?: string) => {
    const newItem: FamilyVM = {
      ...itemSelected,
      cfControl: value,
    };

    let practicesNew: Array<FamilyVM> = values?.slice() || [];

    const isSelectedPractice = !!values?.find(
      (item) =>
        item.fiscalCode === itemSelected.fiscalCode &&
        item.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
        item.idIntestatario === itemSelected.idIntestatario
    );

    if (isSelectedPractice) {
      practicesNew = practicesNew.map((operation) =>
        !(
          operation.fiscalCode === itemSelected.fiscalCode &&
          operation.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
          operation.idIntestatario === itemSelected.idIntestatario
        )
          ? operation
          : newItem
      ) as Array<FamilyVM>;

      onChange(practicesNew);
      return;
    }

    practicesNew.push(newItem);
    onChange(practicesNew);
  });

  const body = useMemo(() => {
    return values?.slice().map((item) => {
      const element = { ...item };

      const disabledRegister =
        typeRegister === TypeRegister.Unavailable ||
        typeRegister === TypeRegister.UntraceableCensus;

      return {
        ...item,
        disabledCheckbox: item.richiedente && disabledRegister,
        checked: item.selectedFromDb || item.checked || false,
        relationshipElement:
          item.tipoLegame === '4' ? (
            <CohabitationBondList
              id={`${item.fiscalCode}`}
              label=""
              value={item.relationship || ''}
              onChange={(value) => onChangeRelationship(element, value)}
              disabled={disabled || (item.richiedente && disabledRegister)}
            />
          ) : (
            <ParenthoodRelationshipList
              id={`${item.fiscalCode}`}
              label=""
              value={item.relationship || ''}
              isPractice
              filterParenthood={filterParenthood}
              onChange={(value) => onChangeRelationship(element, value)}
              disabled={disabled || (item.richiedente && disabledRegister)}
              isHeadParenthood={
                !!values?.find((item1) => {
                  if (
                    !(
                      item1.fiscalCode === item.fiscalCode &&
                      item1.idSchedaSoggettoANPR === item.idSchedaSoggettoANPR &&
                      item1.idIntestatario === item.idIntestatario
                    )
                  ) {
                    return item1.relationship?.toString() === '1';
                  }
                  return false;
                })
              }
            />
          ),
        drivingLicenseElement: (
          <EnrollmentList
            id={item.fiscalCode}
            value={item.drivingLicense || ''}
            onChange={(value) => onChangeDriving(item, value)}
            disabled={disabledAll || (item.richiedente && disabledRegister)}
            label=""
          />
        ),
        autoElement: (
          <EnrollmentList
            id={item.fiscalCode}
            disabled={disabledAll || (item.richiedente && disabledRegister)}
            value={item.auto || ''}
            onChange={(value) => onChangeAuto(item, value)}
            label=""
          />
        ),
        aireElement: (
          <AireList disabled id={item.fiscalCode} value={item.aire} onChange={noop} label="" />
        ),
        cfControlElement: (
          <CfControlList
            label=""
            id={item.fiscalCode}
            disabled={disabledResidente || (item.richiedente && disabledRegister)}
            value={item.cfControl || ''}
            onChange={(value) => onChangeCfControl(item, value)}
          />
        ),
        action: (
          <IconButton
            aria-label="Modifica generalità"
            aria-haspopup="true"
            onClick={() => {
              onEditPress(item);
            }}
            disabled={!item.isEditGenerality}
            style={{
              padding: '4px',
            }}
          >
            <Edit />
          </IconButton>
        ),
      };
    });
  }, [
    values,
    onChangeRelationship,
    onChangeDriving,
    onChangeAuto,
    onChangeCfControl,
    disabledResidente,
    typeRegister,
  ]);

  const onCheckPress = useCallback(
    (itemSelected) => {
      let practicesNew = values?.slice() || [];

      practicesNew = practicesNew.map((operation) =>
        operation.fiscalCode === itemSelected.fiscalCode &&
        operation.idSchedaSoggettoANPR === itemSelected.idSchedaSoggettoANPR &&
        operation.idIntestatario === itemSelected.idIntestatario
          ? { ...operation, checked: !operation.checked }
          : operation
      );

      onChange(practicesNew);
    },
    [onChange, values]
  );

  const onSelectAll = useCallback(
    (selected) => {
      const functionalitiesNew = values.slice().map((practice) => ({
        ...practice,
        checked: selected,
      }));

      onChange(functionalitiesNew);
    },
    [onChange, values]
  );

  const count = useMemo(() => {
    return values.reduce((prev, next) => prev + (next.checked || next.selectedFromDb ? 1 : 0), 0);
  }, [values]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            ...(count >= 0 && {
              bgcolor: (theme) =>
                alpha(theme.palette.cdmBrandThird.main, theme.palette.action.activatedOpacity),
            }),
          }}
        >
          <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
            {count} {count === 1 ? 'selezionato' : 'selezionati'}
          </Typography>
        </Toolbar>
        <TableCheckbox
          minWidth={600}
          head={headCellsPracticesAssign}
          body={body}
          onCheckPress={onCheckPress}
          onSelectAll={onSelectAll}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

TableFamilyFormComponent.displayName = 'Table family form component';

const TableFamilyForm = memo(TableFamilyFormComponent);

export default TableFamilyForm;
